/*
General purpose data methods

Mainly from \\WebReports\scripts\util\data_util.js
with styling standardized
 */
import isArray from 'lodash/isArray'
// this function is copied from Insight
function deepFreeze (object) {
  // Retrieve the property names defined on object
  var propNames = Object.getOwnPropertyNames(object)

  // Freeze properties before freezing self

  for (let name of propNames) {
    let value = object[name]

    if (value && typeof value === 'object') {
      deepFreeze(value)
    }
  }

  return Object.freeze(object)
}

function isNumeric (n) {
  return !isNaN(parseFloat(n)) && isFinite(n)
}

function isNonNegative (n) {
  return isNumeric(n) && n >= 0
}

function isObject (s) {
  return typeof (s) === 'object' && s !== null && !isArray(s)
}

function yearRangeForReportYear (reportYear) {
  if (!isNumeric(reportYear) || +reportYear <= 0) return ''
  var endYear = +reportYear
  var endYearStr = '' + endYear
  var startYear = endYear - 1
  return `${startYear}-${endYearStr.slice(endYearStr.length - 2)}`
}

function bnForDbn (dbn) {
  if (!dbn || dbn.length !== 6) return dbn
  return dbn.slice(2)
}

let boroughLookup = {
  'K': 'Brooklyn',
  'M': 'Manhattan',
  'Q': 'Queens',
  'R': 'Staten Island',
  'X': 'Bronx'
}

function boroughForDbn (dbn) {
  if (!dbn || dbn.length !== 6) return undefined
  return boroughLookup[dbn[2]]
}

function isCharterDbn (dbn) {
  if (!dbn || dbn.length !== 6) return false
  return dbn.slice(0, 2) === '84'
}

function ratingForScore (s) {
  return s ? Math.floor(s) : undefined
}

// "missing" is null, undefined or empty string (for null number values converted to strings)
//  - should not be used on strings that can be empty
//  - allows val=0, val=true, etc. as not missing
function valIsMissing (val) { return (val == null || val === '') }

function valIsNA (val) { return (val === 'N/A') }

// If string is like "N<X" - N Less Than
//  - case insensitive, spaces allowed
//  - X assumed positive integer, numbers only (no commas)
const reNLT = /^\s*n\s*<\s*\d+\s*$/i
function valIsNLT (val) {
  return reNLT.test(val)
}

function valIsPara (val) {
  return val === '&#182;'
}

function capitalizeFirstLetter (s) {
  return s.charAt(0).toUpperCase() + s.substr(1)
}

function formatValue (value, format) {
  if (valIsMissing(value)) return '' // Missing values as blanks, could also be N/A?

  var absvalue = Math.abs(value)
  var vals = '' + absvalue
  switch (format) {
    case '':
      vals = '' + absvalue
      break
    case 's':
      vals = '' + value
      break
    case 'd0':
      vals = Math.round(absvalue).toString()
      break
    case 'd0.0':
    case 'd+0.0':
      vals = '' + absvalue.toFixed(1)
      break
    case 'd0.00':
    case 'd+0.00':
      vals = '' + absvalue.toFixed(2)
      break
    case 'd0.000':
    case 'd+0.000':
      vals = '' + absvalue.toFixed(3)
      break

    // Incoming data is 0-1 scale, converted to %

    // Add a small Number.EPSILON to absValue to ensure that, after multiplying by 100,
    // the result is never smaller than the intended value 100 * x. Here, x refers to the
    // target value — either the real value we intend (e.g., x = 0.145), or any conceptual
    // value whose difference from the stored floating-point number is bounded by at most
    // half of the largest ULP anywhere in the range [0, 1].
    //
    // For values that have already been pre-rounded, this ensures that after multiplying
    // by 100, the result is always >= 100 * x.
    //
    // Example: x = 0.145 would give x * 100 = 14.499999999999998 and round to 14.
    // Adding Number.EPSILON ensures (x + Number.EPSILON) * 100 is at least 14.5.
    //
    // See discussion under PR #295 for additional details.
    case 'p1.00':
    case 'p+1.00':
      vals = ((absvalue + Number.EPSILON) * 100).toFixed(0) + '%'
      break
    case 'p1.000':
    case 'p+1.000':
      vals = ((absvalue + Number.EPSILON) * 100).toFixed(1) + '%'
      break
    case 'p1.0000':
    case 'p+1.0000':
      vals = ((absvalue + Number.EPSILON) * 100).toFixed(2) + '%'
      break

    // Incoming data is 0-100 scale, converted to %
    case 'p100':
    case 'p+100':
      vals = absvalue.toFixed(0) + '%'
      break
    case 'p100.0':
    case 'p+100.0':
      vals = absvalue.toFixed(1) + '%'
      break
    default:
      vals = '' + absvalue
  }

  if (value < 0) {
    vals = '-' + vals
  } else {
    switch (format) {
      case 'd+0.0':
      case 'd+0.00':
      case 'p+1.00':
      case 'p+1.000':
      case 'p+100':
      case 'p+100.0':
        vals = '+' + vals
        break
    }
  }

  return vals
}

/**********/
/* d3Nest */
/**********/

// Converts a d3 nest (array of {key:, values:[]}) into a map of the keys directly to the values
//   If uniqueFlag=true, it assumes only one value per key, and maps that directly
function d3NestToMap (nestData, uniqueFlag) {
  var mapData = {}
  nestData.forEach(function (d) {
    if (uniqueFlag) {
      if (d.values.length > 1) console.log('Warning: d3NestToMapUnique data not unique for key ' + d.key)
      mapData[d.key] = d.values[0]
    } else {
      mapData[d.key] = d.values
    }
  })
  return mapData
}
function d3NestToMapUnique (nestData) { return d3NestToMap(nestData, true) }

// Similar to d3NestToMap, but recursively converts nested layers of keys into nested maps
// If levels < 0  it converts all levels of nesting; otherwise it only converts
//  the top given number of levels, while leaving the deeper levels as d3 nests
function d3NestToMapRecurse (nestData, levels = -1) {
  if (levels === 0 ||
      nestData.length === 0 ||
      !nestData[0].hasOwnProperty('key') ||
      !nestData[0].hasOwnProperty('values')) {
    // Not a valid nest, or reached end of recursion and no more nested data
    return nestData
  }
  var mapData = {}
  var nextLevels = levels < 0 ? levels : levels - 1
  nestData.forEach(function (d) { mapData[d.key] = d3NestToMapRecurse(d.values, nextLevels) })
  return mapData
}

/***************/
/* SQR-related */
/***************/

let schoolTypeLookup = {
  'City': 'ALL',
  'District': 'ALL',
  '4410': 'Pre-K',
  'NYCEEC': 'Pre-K',
  'Pre-K Center': 'Pre-K',
  'NYCPS Pre-K Center': 'Pre-K',
  'District Pre-K': 'Pre-K',
  'CBO Pre-K': 'Pre-K',
  'Charter Pre-K': 'Pre-K',
  'Elementary': 'ES',
  'Middle': 'MS',
  'High School': 'HS',
  'K-8': 'K-8',
  'High School Transfer': 'HST',
  'K-1': 'EC',
  'K-2': 'EC',
  'K-3': 'EC',
  'D75': 'D75',
  'YABC': 'YABC'
}

function schoolTypeCodeForName (schoolTypeName) {
  return schoolTypeLookup[schoolTypeName]
}

const frElementNameLookup = {
  // added for 2024
  'IP': 'Instruction and Performance',
  'SS': 'Safety and School Climate',
  'RF': 'Relationships with Families',
  // existed before 2024
  'CT': 'Collaborative Teachers',
  'ES': 'Effective School Leadership',
  'RI': 'Rigorous Instruction',
  'SE': 'Supportive Environment',
  'SF': 'Strong Family-Community Ties',
  'TR': 'Trust',
  'SA': 'Student Achievement'
}

function frElementNameForCode (elementCode) {
  return frElementNameLookup[elementCode]
}

function colorClassForCode (elementCode) {
  return 'color-' + elementCode.toLowerCase()
}

// HS and any other unknown types will be left as is
const schoolTypeRenames = {
  'D75': 'District 75',
  'Elementary': 'Elementary School',
  'High School Transfer': 'Transfer High School',
  'K-1': 'Early Childhood School',
  'K-2': 'Early Childhood School',
  'K-3': 'Early Childhood School',
  'K-8': 'K-8 School',
  'Middle': 'Middle School',
  'YABC': 'Young Adult Borough Center',
  'NYCEEC': 'NYCEEC Pre-K'
}

function schoolTypeDisplayForType (schoolType) {
  return schoolTypeRenames[schoolType] || schoolType || ''
}

function dbnReportTypeForSchool (s) {
  if (!s || !s['dbn'] || !s['report_type']) return undefined
  return s['dbn'] + '_' + s['report_type']
}

function schoolsAreEqual (a, b) {
  var equal
  if (a && b && !a.dbn && !b.dbn) {
    equal = true
  } else if (!a || !b || !a.dbn || !b.dbn) {
    equal = false
  } else if (!a.report_type || !b.report_type) {
    equal = (a.dbn === b.dbn)
  } else {
    equal = (a.dbn === b.dbn) && (a.report_type === b.report_type)
  }
  return equal
}

function schoolIsDistrictPK (school) {
  return school && school.dbn &&
    school.report_type === 'PK' &&
    school.school_type === 'District Pre-K'
}

function schoolReportURL (reportPrefix, year, school) {
  if (!school || !school.dbn) return undefined
  var y = year
  var yearRange = yearRangeForReportYear(y)
  var rt = school.report_type
  var bn = bnForDbn(school.dbn)
  return 'https://www.nycenet.edu/OA/SchoolReports/' + yearRange + '/' + reportPrefix + '_' + y + (rt ? '_' + rt : '') + '_' + bn + '.pdf'
}

function qrURL (school, year) {
  if (!school || !school.dbn || !year) {
    return 'http://schools.nyc.gov/Accountability/tools/review/default.htm'
  }
  return schoolReportURL('Quality_Review', year, {dbn: school.dbn})
}

function sqgURL (year, school) {
  let url = '/guide/'
  if (!year) return url
  url += `${year}/`

  if (!school || !school.dbn) return url
  url += `#dbn=${school.dbn}`

  if (school.report_type && !schoolIsDistrictPK(school)) {
    url += `&report_type=${school.report_type}`
  }

  return url
}

function scoringGuideURL (year, school) {
  if (!school || !school.dbn || !school.report_type) return 'https://www.schools.nyc.gov/about-us/reports/school-quality'
  const y = year
  const yearRange = yearRangeForReportYear(y)
  const rt = school.report_type
  const scoringGuideNaming = (y >= 2025) ? '_SQR_Scoring_Guide_' : '_School_Quality_Guide_'
  return 'https://www.nycenet.edu/OA/SchoolReports/' + yearRange + '/' + school.dbn + scoringGuideNaming + y + '_' + rt + '.pdf'
}
function dashboardURL (school) {
  return school && school.dbn && school.report_type
    ? `/dashboard/#dbn=${school.dbn}&report_type=${school.report_type}` : `/dashboard/`
}

function schoolFinderURL (school) {
  // New site currently requires mapping to id so for now just always send them
  //  to default site
  const baseUrl = 'https://myschools.nyc/schools/high-school'
  return school && school.dbn
    ? baseUrl : baseUrl
}

function composePanoramaURL (panoramaId, projectId) {
  let url = 'https://secure.panoramaed.com/nycdoe/understand'

  if (!valIsMissing(panoramaId)) {
    url += '/' + panoramaId + '/summary'
  }

  url += '?auth_token=NeMqBRG7xyKtyeqmK_Nd'

  if (projectId) {
    /* this makes a difference only if panorama id is in the URL (i.e. if a school is selected) */
    url += '&project_id=' + projectId
  }

  return url
}

function surveyRecognitionURL (year) {
  return `https://infohub.nyced.org/docs/default-source/default-document-library/${year}-response-rate-recognition-awardees-list-accessible.pdf`
}
/***************************************/
/* Export only public facing functions */
/***************************************/

export {
  deepFreeze,
  isNumeric,
  isNonNegative,
  isObject,
  bnForDbn,
  boroughForDbn,
  isCharterDbn,
  yearRangeForReportYear,
  ratingForScore,
  valIsMissing,
  valIsNA,
  valIsNLT,
  valIsPara,
  capitalizeFirstLetter,
  formatValue,

  d3NestToMap,
  d3NestToMapUnique,
  d3NestToMapRecurse,

  schoolTypeCodeForName,
  frElementNameForCode,
  colorClassForCode,
  schoolTypeDisplayForType,
  dbnReportTypeForSchool,
  schoolsAreEqual,
  schoolIsDistrictPK,
  qrURL,
  sqgURL,
  scoringGuideURL,
  dashboardURL,
  schoolFinderURL,
  composePanoramaURL,
  surveyRecognitionURL
}



// WEBPACK FOOTER //
// ./src/utils/data.js